import { useState, type FormEvent } from "react";
import type { Vehicle } from "@fleetlease/shared";
import { useList, useCreate, useUpdate, money } from "./data";
import { PageHeader, Button, Table, Modal, Field, Input, Select, useForm, FormError } from "./ui";
import { TableSkeleton } from "./ui";
import { Badge } from "./Badge";

const tone = (s: string) => (s === "AVAILABLE" ? "info" : s === "RENTED" ? "warn" : s === "IN_SHOP" ? "danger" : "info");
const blank = { make: "", model: "", year: "2023", vin: "", color: "", plate: "", odometer: "0", fuelType: "GAS", monthlyRate: "", status: "AVAILABLE" };

export function Fleet() {
  const q = useList<Vehicle>("vehicles", "/vehicles");
  const create = useCreate<any>("/vehicles", ["vehicles", "shop"]);
  const update = useUpdate<any>((id) => `/vehicles/${id}`, ["vehicles", "shop"]);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);
  const f = useForm({ ...blank });
  const mut = editing ? update : create;

  function openCreate() { setEditing(null); f.setValues({ ...blank }); setOpen(true); }
  function openEdit(v: Vehicle) {
    setEditing(v);
    f.setValues({ make: v.make, model: v.model, year: String(v.year), vin: v.vin, color: v.color ?? "", plate: v.plate ?? "", odometer: String(v.odometer), fuelType: v.fuelType, monthlyRate: String(v.monthlyRateCents / 100), status: v.status });
    setOpen(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body: any = {
      make: f.values.make, model: f.values.model, year: Number(f.values.year),
      color: f.values.color || undefined, plate: f.values.plate || undefined,
      odometer: Number(f.values.odometer), fuelType: f.values.fuelType,
      monthlyRateCents: Math.round(Number(f.values.monthlyRate) * 100), status: f.values.status,
    };
    if (editing) await update.mutateAsync({ id: editing.id, body });
    else await create.mutateAsync({ ...body, vin: f.values.vin.toUpperCase() });
    setOpen(false);
  }

  const rows = (q.data?.items ?? []).filter((v) => (v as any).type === "Vehicle");

  return (
    <div className="space-y-4">
      <PageHeader title="Fleet" action={<Button onClick={openCreate}>Add vehicle</Button>} />
      {q.isPending ? <TableSkeleton cols={6} /> : (
        <Table<Vehicle>
          rows={rows} empty="No vehicles yet — add your first."
          columns={[
            { header: "Vehicle", cell: (v) => `${v.year} ${v.make} ${v.model}${v.color ? ` · ${v.color}` : ""}` },
            { header: "Plate", cell: (v) => v.plate ?? "—" },
            { header: "Odometer", cell: (v) => `${v.odometer.toLocaleString()} mi` },
            { header: "Monthly", cell: (v) => money(v.monthlyRateCents) },
            { header: "Status", cell: (v) => <Badge tone={tone(v.status)}>{v.status}</Badge> },
            { header: "", cell: (v) => <button onClick={() => openEdit(v)} className="text-accent hover:underline text-sm">Edit</button> },
          ]}
        />
      )}

      <Modal title={editing ? "Edit vehicle" : "Add vehicle"} open={open} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <FormError error={mut.error} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Make"><Input required value={f.values.make} onChange={(e) => f.set("make", e.target.value)} /></Field>
            <Field label="Model"><Input required value={f.values.model} onChange={(e) => f.set("model", e.target.value)} /></Field>
            <Field label="Year"><Input type="number" required value={f.values.year} onChange={(e) => f.set("year", e.target.value)} /></Field>
            <Field label="Color"><Input value={f.values.color} onChange={(e) => f.set("color", e.target.value)} /></Field>
          </div>
          <Field label="VIN (17 chars)"><Input required={!editing} disabled={!!editing} minLength={17} maxLength={17} value={f.values.vin} onChange={(e) => f.set("vin", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plate"><Input value={f.values.plate} onChange={(e) => f.set("plate", e.target.value)} /></Field>
            <Field label="Odometer"><Input type="number" value={f.values.odometer} onChange={(e) => f.set("odometer", e.target.value)} /></Field>
            <Field label="Fuel type">
              <Select value={f.values.fuelType} onChange={(e) => f.set("fuelType", e.target.value)}>
                {["GAS", "DIESEL", "HYBRID", "ELECTRIC", "OTHER"].map((x) => <option key={x}>{x}</option>)}
              </Select>
            </Field>
            <Field label="Monthly rate ($)"><Input type="number" required value={f.values.monthlyRate} onChange={(e) => f.set("monthlyRate", e.target.value)} /></Field>
          </div>
          <Field label="Status">
            <Select value={f.values.status} onChange={(e) => f.set("status", e.target.value)}>
              {["AVAILABLE", "RESERVED", "RENTED", "IN_SHOP", "INACTIVE"].map((x) => <option key={x}>{x}</option>)}
            </Select>
          </Field>
          <div className="mt-2 flex justify-end"><Button type="submit" disabled={mut.isPending}>{mut.isPending ? "Saving…" : editing ? "Save changes" : "Save vehicle"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
